import type { ReactNode } from "react";
import { Accordion, ActionIcon, Box, CopyButton, Group, SimpleGrid, Text, Tooltip } from "@mantine/core";
import { SectionCard, spacePx } from "@xplatform/design-system";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import type { FlowConfigViewModel } from "@models/doctype";
import { useTranslation } from "@hooks/useTranslation";
import { flowTypeDescription, translateFlowType } from "@utils/flowTypeTranslations";
import "./FlowSummary.scss";

interface FlowSummaryProps {
    flowConfig: FlowConfigViewModel;
    docTypeCode?: string;
}

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
    <Box className="flow-summary__fact">
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">{label}</Text>
        <Box mt={spacePx[1]}>{children}</Box>
    </Box>
);

const CopyableId = ({ label, value }: { label: string; value: string }) => {
    const { t } = useTranslation();
    return (
        <Group gap="xs" wrap="nowrap" className="flow-summary__id">
            <Text size="xs" c="dimmed" w={110} style={{ flexShrink: 0 }}>{label}</Text>
            <Text size="xs" ff="monospace" truncate title={value}>{value}</Text>
            <CopyButton value={value} timeout={1500}>
                {({ copied, copy }) => (
                    <Tooltip label={copied ? t("admin.flowConfigs.summary.copied") : t("admin.flowConfigs.summary.copy")}>
                        <ActionIcon
                            size="sm"
                            variant="subtle"
                            color={copied ? "green" : "gray"}
                            onClick={copy}
                            aria-label={t("admin.flowConfigs.summary.copyLabel", { label })}
                        >
                            {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                        </ActionIcon>
                    </Tooltip>
                )}
            </CopyButton>
        </Group>
    );
};

/**
 * What a reader needs about a flow version, most useful first: its type,
 * when and by whom it last changed, its description and flow-wide options.
 * The raw IDs are still one click away, with copy buttons, for support work.
 */
export const FlowSummary = ({ flowConfig, docTypeCode }: FlowSummaryProps) => {
    const { t } = useTranslation();
    const preprocessing = flowConfig.preprocessingOptions;
    const runtimeDefault = t("admin.flowConfigs.summary.runtimeDefault");
    const onOff = (value: boolean | null | undefined) =>
        value == null ? runtimeDefault : value ? t("admin.flowConfigs.options.enabled") : t("admin.flowConfigs.options.disabled");

    return (
        <SectionCard className="flow-summary">
            <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="lg">
                <Fact label={t("admin.table.columns.type")}>
                    <Tooltip label={flowTypeDescription(flowConfig.flowType, t)} multiline maw={360} disabled={!flowTypeDescription(flowConfig.flowType, t)}>
                        <Text size="sm" fw={500} className="flow-summary__type">{translateFlowType(flowConfig.flowType, t)}</Text>
                    </Tooltip>
                </Fact>
                <Fact label={t("admin.table.columns.updated")}>
                    <Text size="sm">{flowConfig.updatedAt?.toLocaleString() ?? "-"}</Text>
                    {flowConfig.updatedBy && (
                        <Text size="xs" c="dimmed" truncate title={flowConfig.updatedBy}>
                            {t("admin.flowConfigs.summary.by", { user: flowConfig.updatedBy })}
                        </Text>
                    )}
                </Fact>
                <Fact label={t("admin.table.columns.created")}>
                    <Text size="sm">{flowConfig.createdAt?.toLocaleString() ?? "-"}</Text>
                </Fact>
                <Fact label={t("admin.flowConfigs.summary.preprocessing")}>
                    <Text size="sm">
                        {t("admin.flowConfigs.summary.stripLinks", { value: onOff(preprocessing?.strip_pdf_links) })}
                    </Text>
                </Fact>
            </SimpleGrid>

            {flowConfig.flowDescription && (
                <Text size="sm" mt="md" className="flow-summary__description">{flowConfig.flowDescription}</Text>
            )}

            <Accordion variant="default" chevronPosition="left" mt="xs" mb="-xs" className="flow-summary__technical">
                <Accordion.Item value="ids">
                    <Accordion.Control>
                        <Text size="xs" c="dimmed" fw={600}>{t("admin.flowConfigs.summary.technical")}</Text>
                    </Accordion.Control>
                    <Accordion.Panel>
                        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs">
                            <CopyableId label={t("admin.flowConfigs.summary.configId")} value={flowConfig.id} />
                            <CopyableId label={t("admin.flowConfigs.summary.flowBaseId")} value={flowConfig.flowBaseId} />
                            <CopyableId label={t("admin.flowConfigs.summary.docTypeId")} value={flowConfig.docTypeId} />
                            {docTypeCode && <CopyableId label={t("admin.flowConfigs.summary.docTypeCode")} value={docTypeCode} />}
                        </SimpleGrid>
                    </Accordion.Panel>
                </Accordion.Item>
            </Accordion>
        </SectionCard>
    );
};
